import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Phone, Copy, Check } from 'lucide-react';
import { companyInfo } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const FloatingContact = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useLanguage();

  const copyWechat = async () => {
    try {
      await navigator.clipboard.writeText(companyInfo.wechat);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // silent fail
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3" data-testid="floating-contact">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6 w-[340px] max-w-[calc(100vw-3rem)]"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-navy-900 text-lg">
                {t('Talk to us', '联系我们')}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
                aria-label={t('Close', '关闭')}
                data-testid="close-floating"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-5">
              {t(
                'Free bilingual consultation. Choose your preferred contact method.',
                '免费双语咨询。选择您方便的联系方式。'
              )}
            </p>

            {/* Phone */}
            <a
              href={`tel:${companyInfo.phoneRaw}`}
              className="flex items-center gap-3 p-4 rounded-2xl border border-gray-200 hover:border-eucalyptus-300 hover:bg-eucalyptus-50 transition-all mb-3 group"
              data-testid="floating-phone"
            >
              <div className="w-11 h-11 rounded-xl bg-eucalyptus-100 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-eucalyptus-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">{t('Call us', '致电我们')}</div>
                <div className="font-semibold text-navy-900 text-[15px]">{companyInfo.phone}</div>
              </div>
            </a>

            {/* WeChat with QR */}
            <div className="rounded-2xl border-2 border-eucalyptus-300 bg-eucalyptus-50 p-4" data-testid="floating-wechat">
              <div className="flex items-start gap-4">
                {/* QR Code */}
                <div className="flex-shrink-0 bg-white p-2 rounded-xl shadow-sm">
                  <img
                    src={companyInfo.wechatQR}
                    alt={`WeChat QR code for ${companyInfo.wechat}`}
                    className="w-[110px] h-[110px] object-cover rounded-lg"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center gap-2 mb-1">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-eucalyptus-700 flex-shrink-0" fill="currentColor" aria-hidden="true">
                      <path d="M8.691 2C4.768 2 1.5 4.68 1.5 8.14c0 1.9 1.15 3.68 2.99 4.86l-.61 1.86 2.24-1.12c.62.09 1.24.14 1.87.14.16 0 .32-.01.48-.02-.09-.34-.14-.7-.14-1.06 0-3.16 2.99-5.72 6.68-5.72.11 0 .22 0 .33.01C14.94 4 12.05 2 8.69 2zm3.53 4.98a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zm-6.42 0a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6zm9.51 1.51c-3.2 0-5.79 2.16-5.79 4.83s2.59 4.83 5.79 4.83c.65 0 1.27-.09 1.86-.24l1.7.94-.47-1.52c1.42-1.06 2.34-2.66 2.34-4.51 0-2.67-2.59-4.83-5.79-4.83zm-1.92 4.03a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4zm3.86 0a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4z"/>
                    </svg>
                    <span className="text-xs text-eucalyptus-800 font-semibold uppercase tracking-wider">
                      {t('WeChat · 微信', '微信')}
                    </span>
                  </div>
                  <div className="font-bold text-navy-900 text-lg font-mono tracking-wide mb-2 break-all" data-testid="floating-wechat-id">
                    {companyInfo.wechat}
                  </div>
                  <button
                    onClick={copyWechat}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white hover:bg-gray-50 border border-eucalyptus-200 text-xs font-semibold text-eucalyptus-700 transition-colors"
                    aria-label={t('Copy WeChat ID', '复制微信号')}
                    data-testid="copy-wechat"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        {t('Copied', '已复制')}
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        {t('Copy ID', '复制ID')}
                      </>
                    )}
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-eucalyptus-800 text-center mt-3 leading-relaxed">
                {t(
                  'Scan QR or search WeChat ID to add us.',
                  '扫码或搜索微信号添加我们。'
                )}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main FAB button */}
      <motion.button
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1, duration: 0.5, type: 'spring' }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-16 h-16 rounded-full bg-eucalyptus-600 hover:bg-eucalyptus-700 text-white shadow-2xl flex items-center justify-center transition-colors"
        aria-label={isOpen ? t('Close contact panel', '关闭联系面板') : t('Open contact panel', '打开联系面板')}
        aria-expanded={isOpen}
        data-testid="floating-contact-toggle"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>

        {!isOpen && (
          <span className="absolute inset-0 rounded-full bg-eucalyptus-500 opacity-40 animate-ping" aria-hidden="true" />
        )}
      </motion.button>
    </div>
  );
};

export default FloatingContact;
